<template>
  <div class="flex h-full flex-col gap-4">
    <TagCreateDialog
      v-model:visibility="dialogCreateVisible"
      @saved="handleNewTagCreated"
      @closed="tagCreateImport = null" />
    <TagManageDialog
      v-model:visibility="dialogManageVisible"
      :tags="availableTags"
      @tag-changed="loadAvailableTags" />

    <div v-if="!showEmptyState" class="flex flex-wrap items-center gap-4">
      <SmInput
        v-model="filters.search"
        outline
        size="medium"
        class="max-w-xs flex-1"
        :label="i18n.t('search')" />

      <SmDropdown :close-on-click-inside="false" class="shrink-0">
        <template #trigger>
          <SmButton outline size="small">
            <div class="flex items-center gap-2">
              <span>{{ i18n.t('filter') }}</span>
              <span v-if="activeFilterCount"> ({{ activeFilterCount }}) </span>
            </div>
          </SmButton>
        </template>

        <div class="flex flex-col gap-4 p-4">
          <div>
            <h4 class="mb-2 font-semibold text-contrast">
              {{ i18n.t('tags') }}
            </h4>
            <SmSelect
              v-model:selection="filters.tags"
              :options="tagOptions"
              :label="i18n.t('chooseTags')"
              :disabled="tagOptions.length === 0"
              :multiselect="true"
              searchable />
          </div>
        </div>
      </SmDropdown>

      <SmButton
        v-require-permission="CompanyAccessRole.ADMIN"
        size="small"
        class="ml-auto shrink-0"
        @click="emit('upload')">
        {{ i18n.t('datev.upload') }}
      </SmButton>
    </div>

    <EmptyState
      v-if="showEmptyState"
      :icon="BookOpen"
      :title="i18n.t('accountingImports')"
      :text="emptyStateText"
      :create-label="i18n.t('datev.upload')"
      :show-docs-btn="false"
      class="flex-1"
      @create="emit('upload')" />

    <div v-else class="min-h-0 flex-1">
      <SmTable
        v-model:data="tableRows"
        :columns="columns"
        :loading="loading"
        :default-sorting="defaultSorting"
        @cell-click="handleTableCellClick"
        key-field="_id"
        class="w-full">
        <template #header-tags>
          <div class="flex items-center gap-2">
            {{ i18n.t('tags') }}
            <Settings
              :size="14"
              class="cursor-pointer"
              @click="dialogManageVisible = true" />
          </div>
        </template>

        <template #name="{ row }">
          <div class="flex flex-col gap-1">
            <TextMultiline :text="row.displayName" />
            <p
              v-if="row.filename && row.filename !== row.displayName"
              class="text-sm text-contrast-muted">
              {{ row.filename }}
            </p>
          </div>
        </template>

        <template #createdAt="{ row }">
          <span>{{ displayDate(new Date(row.created)) }}</span>
        </template>

        <template #tags="{ row }">
          <div
            class="flex w-full cursor-default flex-wrap items-center gap-2 py-2">
            <TagComponent
              v-for="tag in getTagDisplays(row.tags ?? [])"
              :key="tag.id"
              :tag="{ name: tag.name, color: tag.color }"
              :selected="isTagSelected(tag.id)"
              :style="{ maxWidth: 'calc(100% - 2rem)' }"
              class="cursor-pointer"
              @click.capture="handleTagClick(tag.id)" />
            <TagSelect
              show-create-tag
              :available-tags="availableTags"
              :selected="getNormalizedTagIds(row.tags ?? [])"
              @delete-tag="removeTagFromImport($event, row.tags ?? [], row)"
              @add-tag="addTagToImport($event, row.tags ?? [], row)"
              @create-tag="openCreateTagDialog(row)">
              <Plus :size="16" class="tag-add-icon opacity-0 transition" />
            </TagSelect>
          </div>
        </template>

        <template #status="{ row }">
          <div class="flex items-center gap-2">
            <component
              :is="getStatusMeta(row.status).icon"
              :class="['shrink-0', getStatusMeta(row.status).class]"
              :size="20" />
            <span>{{ getStatusLabel(row.status) }}</span>
          </div>
        </template>

        <template #actions="{ row }">
          <SmDropdown>
            <SmDropdownItem :icon="PencilLine" @click="openEditDialog(row)">
              {{ i18n.t('general.edit') }}
            </SmDropdownItem>
            <SmDropdownItem :icon="Loader" @click="emit('reanalyze', row)">
              {{
                row.status === 'uploaded'
                  ? i18n.t('accountingExport.startProcessing')
                  : i18n.t('accountingExport.reanalyze')
              }}
            </SmDropdownItem>
            <SmDropdownItem :icon="Download" @click="downloadImport(row)">
              {{ i18n.t('download') }}
            </SmDropdownItem>
            <SmDropdownItem
              :icon="Trash"
              hover-color="var(--sm-magenta)"
              @click="deleteImport(row._id)">
              {{ i18n.t('delete') }}
            </SmDropdownItem>
          </SmDropdown>
        </template>

        <template #no-data>
          <div class="py-8 text-center text-[color:var(--sm-contrast-muted)]">
            <p v-if="hasActiveFilters">
              {{ i18n.t('noResultsFound') }}
            </p>
            <p v-else>
              {{ i18n.t('noData') }}
            </p>
          </div>
        </template>
      </SmTable>
    </div>

    <AccountingImportEditDialog
      v-if="editDialogVisible"
      v-model:visible="editDialogVisible"
      :import-record="selectedImport"
      :available-tags="availableTags"
      @updated="handleImportUpdated" />
  </div>
</template>

<script setup lang="ts">
  import { computed, onMounted, reactive, ref, watch } from 'vue'
  import {
    Download,
    Trash,
    CircleCheck,
    CircleX,
    PencilLine,
    Plus,
    Settings,
    Loader,
    BookOpen,
    CircleDot,
    Brain,
  } from 'lucide-vue-next'
  import { useI18n } from 'vue-i18n'
  import { useRouter } from 'vue-router'

  import {
    AccountingImportOut,
    AccountingService,
    OpenAPI,
    CompanyAccessRole,
    Tag,
    TagsService,
    RGB_Output,
  } from '@/client'
  import { displayDate } from '@/utils/formatter/date'
  import { Column, Sorting } from '@/components/sm/SmTable.types'
  import SmDropdown from '@/components/sm/SmDropdown.vue'
  import SmDropdownItem from '@/components/sm/SmDropdownItem.vue'
  import SmTable from '@/components/sm/SmTable.vue'
  import SmInput from '@/components/sm/SmInput.vue'
  import SmSelect, { Option } from '@/components/sm/SmSelect.vue'
  import TextMultiline from '@/components/sm/TextMultiline.vue'
  import { sendToast } from '@/components/sm/SmNotification'
  import { capitalize } from '@/utils/formatter/formatter'
  import TagComponent from '@/components/tags/TagComponent.vue'
  import SmButton from '@/components/sm/SmButton.vue'
  import TagSelect from '@/components/tags/TagSelect.vue'
  import TagCreateDialog from '@/components/tags/TagCreateDialog.vue'
  import TagManageDialog from '@/components/tags/TagManageDialog.vue'
  import EmptyState from '@/components/shared/EmptyState.vue'
  import AccountingImportEditDialog from './AccountingImportEditDialog.vue'

  type AccountingImportRow = AccountingImportOut & {
    displayName: string
    createdAt: number
  }

  const i18n = useI18n()
  const router = useRouter()

  const emit = defineEmits<{
    (e: 'upload'): void
    (e: 'reanalyze', value: AccountingImportOut): void
  }>()

  const loading = ref(false)
  const imports = ref<AccountingImportOut[]>([])
  const tableRows = ref<AccountingImportRow[]>([])
  const editDialogVisible = ref(false)
  const selectedImport = ref<AccountingImportOut | null>(null)
  const dialogCreateVisible = ref(false)
  const dialogManageVisible = ref(false)
  const tagCreateImport = ref<AccountingImportRow | null>(null)

  const availableTags = ref<Tag[]>([])

  // ##############################
  // #  Filters
  // ##############################
  const filters = reactive({
    search: '',
    tags: undefined as Option<string>[] | undefined,
  })

  const hasActiveFilters = computed(() => {
    return Boolean(filters.search?.trim()) || Boolean(filters.tags?.length)
  })

  const activeFilterCount = computed(() => {
    return selectedTagValues.value.length
  })

  watch(editDialogVisible, (value) => {
    if (!value) {
      selectedImport.value = null
    }
  })

  function applyFilter() {
    const searchTerm = filters.search.trim().toLowerCase()
    const tagFilters = selectedTagValues.value

    const nextRows: AccountingImportRow[] = (imports.value ?? [])
      .map((item) => ({
        ...item,
        displayName: item.name || item.filename,
        createdAt: new Date(item.created).getTime(),
      }))
      .filter((row) => {
        const matchesSearch = searchTerm
          ? [
              row.displayName,
              row.filename,
              row.description ?? '',
              row.notes ?? '',
              row.tags.join(' '),
            ]
              .join(' ')
              .toLowerCase()
              .includes(searchTerm)
          : true

        const matchesTags = tagFilters.length
          ? tagFilters.every((tag) =>
              row.tags.some((rowTag) => tagMatches(rowTag, tag))
            )
          : true

        return matchesSearch && matchesTags
      })
      .sort((a, b) => b.createdAt - a.createdAt)

    tableRows.value = nextRows
  }

  // ##############################
  // #  Table
  // ##############################

  const defaultSorting: Sorting = {
    by: 'createdAt',
    asc: false,
  }

  const columns = computed<Column<AccountingImportRow>[]>(() => [
    {
      key: 'name',
      label: capitalize(i18n.t('name')),
      width: 2,
    },
    {
      key: 'createdAt',
      label: capitalize(i18n.t('created')),
      width: 1,
      sortFn: (a, b) => a.createdAt - b.createdAt,
    },
    {
      key: 'tags',
      label: capitalize(i18n.t('tags')),
      width: 1,
      sortable: false,
    },
    {
      key: 'status',
      label: capitalize(i18n.t('status')),
      width: 1,
      sortFn: (a, b) => statusPriority(a.status) - statusPriority(b.status),
    },
    {
      key: 'actions',
      label: '',
      width: '90px',
      sortable: false,
    },
  ])

  function statusPriority(status: AccountingImportOut['status']) {
    switch (status) {
      case 'uploaded':
        return 1
      case 'in_progress':
        return 2
      case 'ai_prediction_in_progress':
        return 3
      case 'done':
        return 4
      case 'error':
        return 0
      default:
        return -1
    }
  }

  function getStatusMeta(status: AccountingImportOut['status']) {
    switch (status) {
      case 'uploaded':
        return { icon: CircleDot, class: 'text-contrast-muted' }
      case 'done':
        return { icon: CircleCheck, class: 'text-green' }
      case 'in_progress':
        return { icon: Loader, class: 'text-orange animate-spin' }
      case 'ai_prediction_in_progress':
        return { icon: Brain, class: 'text-purple animate-spin' }
      case 'error':
        return { icon: CircleX, class: 'text-red' }
      default:
        return { icon: Loader, class: 'text-orange animate-spin' }
    }
  }

  function getStatusLabel(status: AccountingImportOut['status']) {
    if (!status) return ''
    const key = `accountingImport.status.${status}`
    const translated = i18n.t(key)
    if (translated !== key) return translated
    return capitalize(status.replace(/_/g, ' '))
  }

  // ##############################
  // #  Tags
  // ##############################

  interface TagDisplay {
    id: string
    name: string
    color: RGB_Output
  }

  const defaultTagColor: RGB_Output = { r: 120, g: 120, b: 120 }

  const tagLookupById = computed<Record<string, Tag>>(() =>
    availableTags.value.reduce(
      (acc, tag) => {
        acc[tag._id] = tag
        return acc
      },
      {} as Record<string, Tag>
    )
  )

  const tagLookupByName = computed<Record<string, Tag>>(() =>
    availableTags.value.reduce(
      (acc, tag) => {
        acc[tag.name] = tag
        return acc
      },
      {} as Record<string, Tag>
    )
  )

  const tagOptions = computed<Option<string>[]>(() => {
    return availableTags.value.map((tag) => ({
      label: tag.name,
      value: tag._id,
    }))
  })

  const selectedTagValues = computed(() => {
    return filters.tags?.map((tag) => tag.value) ?? []
  })

  const activeTagKeys = computed(() => {
    const keys = new Set<string>()
    ;(filters.tags ?? []).forEach((option) => {
      keys.add(option.value)
      const byId = tagLookupById.value[option.value]
      if (byId) {
        keys.add(byId._id)
        keys.add(byId.name)
      }
      const byName = tagLookupByName.value[option.value]
      if (byName) {
        keys.add(byName._id)
        keys.add(byName.name)
      }
    })
    return keys
  })

  watch([imports, () => filters.search, selectedTagValues], () => {
    applyFilter()
  })

  watch(tagOptions, () => {
    if (!filters.tags || filters.tags.length === 0) return

    const available = new Set<string>()
    availableTags.value.forEach((tag) => {
      available.add(tag._id)
      available.add(tag.name)
    })

    filters.tags = filters.tags.filter((option) => available.has(option.value))
  })

  function normalizeTagValue(tagValue: string): string {
    if (tagLookupById.value[tagValue]) {
      return tagLookupById.value[tagValue]._id
    }
    if (tagLookupByName.value[tagValue]) {
      return tagLookupByName.value[tagValue]._id
    }
    return tagValue
  }

  function getNormalizedTagIds(tagValues: string[] = []): string[] {
    const unique = new Set<string>()
    return tagValues
      .map((value) => normalizeTagValue(value))
      .filter((value) => {
        if (unique.has(value)) return false
        unique.add(value)
        return true
      })
  }

  function getTagDisplays(tagValues: string[] = []): TagDisplay[] {
    const unique = new Set<string>()
    return tagValues
      .map<TagDisplay>((value) => {
        const tagById = tagLookupById.value[value]
        if (tagById) {
          return {
            id: tagById._id,
            name: tagById.name,
            color: tagById.color,
          }
        }

        const tagByName = tagLookupByName.value[value]
        if (tagByName) {
          return {
            id: tagByName._id,
            name: tagByName.name,
            color: tagByName.color,
          }
        }

        return {
          id: value,
          name: value,
          color: defaultTagColor,
        }
      })
      .filter((tag) => {
        if (unique.has(tag.id)) return false
        unique.add(tag.id)
        return true
      })
  }

  function handleTagClick(tagValue: string) {
    const normalized = normalizeTagValue(tagValue)
    const tag = tagLookupById.value[normalized]
    const option: Option<string> = {
      label: tag ? tag.name : normalized,
      value: normalized,
    }

    const current = filters.tags ?? []
    const exists = current.some((tagOption) => tagOption.value === option.value)

    filters.tags = exists
      ? current.filter((tagOption) => tagOption.value !== option.value)
      : [...current, option]
  }

  function tagMatches(rowTag: string, filterTag: string): boolean {
    if (rowTag === filterTag) return true

    const filterById = tagLookupById.value[filterTag]
    if (
      filterById &&
      (rowTag === filterById._id || rowTag === filterById.name)
    ) {
      return true
    }

    const filterByName = tagLookupByName.value[filterTag]
    if (
      filterByName &&
      (rowTag === filterByName._id || rowTag === filterByName.name)
    ) {
      return true
    }

    const rowById = tagLookupById.value[rowTag]
    if (rowById && (filterTag === rowById._id || filterTag === rowById.name)) {
      return true
    }

    const rowByName = tagLookupByName.value[rowTag]
    if (
      rowByName &&
      (filterTag === rowByName._id || filterTag === rowByName.name)
    ) {
      return true
    }

    return false
  }

  const showEmptyState = computed(
    () => !loading.value && imports.value.length === 0
  )

  const emptyStateText = computed(
    () =>
      `${i18n.t('datevUploadDescription')} ${i18n.t('datevImportDescription')}`
  )

  function handleTableCellClick(
    iRow: number,
    columnKey: string,
    row: AccountingImportRow
  ) {
    if (
      columnKey === 'actions' ||
      columnKey === 'tags' ||
      columnKey === 'selection'
    ) {
      return
    }
    router.push({
      name: 'AccountingImportDetail',
      params: { id: row._id },
    })
  }

  function isTagSelected(tagValue: string) {
    if (activeTagKeys.value.has(tagValue)) return true

    const tagById = tagLookupById.value[tagValue]
    if (tagById && activeTagKeys.value.has(tagById.name)) return true

    const tagByName = tagLookupByName.value[tagValue]
    if (tagByName && activeTagKeys.value.has(tagByName._id)) return true

    return false
  }

  function openCreateTagDialog(row: AccountingImportRow) {
    tagCreateImport.value = row
    dialogCreateVisible.value = true
  }

  async function handleNewTagCreated(tag: Tag) {
    dialogCreateVisible.value = false
    await loadAvailableTags()
    if (tagCreateImport.value) {
      await addTagToImport(
        tag._id,
        tagCreateImport.value.tags ?? [],
        tagCreateImport.value
      )
      tagCreateImport.value = null
    }
  }

  function openEditDialog(row: AccountingImportRow) {
    selectedImport.value = row
    editDialogVisible.value = true
  }

  async function handleImportUpdated() {
    await reload()
  }

  async function updateImportTags(importId: string, tags: string[]) {
    try {
      await AccountingService.patchAccountingImportApiV1AccountingAccountingImportsAccountingImportIdPatch(
        {
          accountingImportId: importId,
          requestBody: {
            tags,
          },
        }
      )
      await reload()
    } catch (error) {
      console.error(error)
      sendToast(i18n.t('datev.updateError'), undefined, 'error')
    }
  }

  async function addTagToImport(
    tagId: string,
    existingTags: string[] = [],
    row: AccountingImportRow
  ) {
    const normalized = getNormalizedTagIds(existingTags)
    if (normalized.includes(tagId)) return

    const nextTags = [...normalized, tagId]
    await updateImportTags(row._id, nextTags)
  }

  async function removeTagFromImport(
    tagId: string,
    existingTags: string[] = [],
    row: AccountingImportRow
  ) {
    const normalized = getNormalizedTagIds(existingTags)
    const nextTags = normalized.filter((value) => value !== tagId)
    await updateImportTags(row._id, nextTags)
  }

  // ##############################
  // #  Actions
  // ##############################

  async function downloadImport(accountingImport: AccountingImportRow) {
    try {
      const [serviceData, headerFilename] = await Promise.all([
        AccountingService.downloadAccountingImportFileApiV1AccountingAccountingImportsAccountingImportIdDownloadGet(
          {
            accountingImportId: accountingImport._id,
          }
        ),
        getFilenameFromHead(accountingImport._id),
      ])

      let blob: Blob | null = null
      let filename =
        headerFilename || accountingImport.filename || 'accounting-import.csv'

      if (serviceData !== undefined) {
        blob = new Blob([serviceData as BlobPart], { type: 'text/csv' })
      } else {
        const fetched = await fetchImportBlob(accountingImport._id)
        if (fetched) {
          blob = fetched.blob
          filename =
            extractFilename(fetched.contentDisposition) ||
            filename ||
            'accounting-import.csv'
        }
      }

      if (!blob) {
        throw new Error('No data received for download.')
      }

      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(downloadUrl)
    } catch (error) {
      console.error(error)
      sendToast(i18n.t('accountingExport.downloadError'), undefined, 'error')
    }
  }

  function extractFilename(header: string | null): string | undefined {
    if (!header) return undefined
    const match = /filename\*?=(?:UTF-8''|"?)([^\";]+)/i.exec(header)
    if (match && match[1]) {
      try {
        return decodeURIComponent(match[1])
      } catch {
        return match[1]
      }
    }
    return undefined
  }

  async function getFilenameFromHead(importId: string) {
    try {
      const url = `${OpenAPI.BASE}/api/v1/accounting/accounting-imports/${encodeURIComponent(importId)}/download`
      const headResponse = await fetch(url, {
        method: 'HEAD',
        headers: {
          Authorization: `Bearer ${OpenAPI.TOKEN}`,
          Accept: 'text/csv,application/octet-stream',
        },
      })
      if (!headResponse.ok) return undefined
      const contentDisposition = headResponse.headers.get('content-disposition')
      return extractFilename(contentDisposition)
    } catch {
      return undefined
    }
  }

  async function fetchImportBlob(importId: string) {
    const url = `${OpenAPI.BASE}/api/v1/accounting/accounting-imports/${encodeURIComponent(importId)}/download`
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${OpenAPI.TOKEN}`,
        Accept: 'text/csv,application/octet-stream',
      },
    })
    if (!response.ok) return null
    const contentDisposition = response.headers.get('content-disposition')
    const blob = await response.blob()
    return { blob, contentDisposition }
  }

  async function deleteImport(id: string) {
    try {
      await AccountingService.deleteAccountingImportApiV1AccountingAccountingImportsAccountingImportIdDelete(
        {
          accountingImportId: id,
        }
      )
      sendToast(i18n.t('accountingExport.deleteSuccess'), undefined, 'success')
      await reload()
    } catch (error) {
      console.error(error)
      sendToast(i18n.t('accountingExport.deleteError'), undefined, 'error')
    }
  }

  // ##############################
  // #  Fetching Data
  // ##############################

  onMounted(() => {
    loadAvailableTags()
    reload()
  })

  async function reload() {
    loading.value = tableRows.value.length === 0
    try {
      const response =
        await AccountingService.getAccountingImportsApiV1AccountingAccountingImportsGet(
          {}
        )
      imports.value = response
      applyFilter()
    } catch (error) {
      console.error(error)
      sendToast(i18n.t('failedToLoadData'), undefined, 'error')
    } finally {
      loading.value = false
    }
  }

  async function loadAvailableTags() {
    try {
      const tags = await TagsService.getTagsApiV1ManagementTagsGet({})
      availableTags.value = tags
    } catch (error) {
      console.error(error)
    }
  }

  defineExpose({ reload })
</script>

<style lang="scss">
  .sm-table-row {
    &:hover {
      .tag-add-icon {
        opacity: 1;
      }
    }
  }
</style>
